import React, { useMemo } from 'react';
import { Card, Typography, Collapse, Tag, Empty, Spin } from 'antd';
import { MdCheck, MdClose, MdDashboard, MdShoppingCart, MdPeople, MdPersonAdd, MdAttachMoney, MdSettings, MdBarChart, MdBuild, MdWarehouse, MdDescription, MdShield, MdCurrencyExchange } from 'react-icons/md';
import { useGetCurrentUser } from '@/hooks/app';

const { Title, Text } = Typography;
const { Panel } = Collapse;

interface PermissionGroup {
  category: string;
  icon: React.ReactNode;
  color: string;
  permissions: {
    key: string;
    label: string;
  }[];
}

const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    category: 'Dashboard',
    icon: <MdDashboard size={20} />,
    color: '#1890ff',
    permissions: [
      { key: 'view_dashboard', label: 'View Dashboard' },
      { key: 'view_dashboard_analytics', label: 'View Dashboard Analytics' },
      { key: 'view_followup', label: 'View Follow-up' },
      { key: 'manage_followup', label: 'Manage Follow-up' },
    ],
  },
  {
    category: 'Quote Management',
    icon: <MdDescription size={20} />,
    color: '#52c41a',
    permissions: [
      { key: 'view_quotes', label: 'View Quotes' },
      { key: 'convert_quote_to_order', label: 'Convert Quote to Order' },
      { key: 'manage_quotes_status', label: 'Manage Quotes Status' },
    ],
  },
  {
    category: 'Order Management',
    icon: <MdShoppingCart size={20} />,
    color: '#fa8c16',
    permissions: [
      { key: 'view_orders', label: 'View Orders' },
      { key: 'manage_payment_status', label: 'Manage Payment Status' },
      { key: 'manage_order_status', label: 'Manage Order Status' },
    ],
  },
  {
    category: 'Customer Management',
    icon: <MdPeople size={20} />,
    color: '#722ed1',
    permissions: [
      { key: 'view_customers', label: 'View Customers' },
      { key: 'create_customers', label: 'Create Customers' },
      { key: 'edit_customers', label: 'Edit Customers' },
      { key: 'delete_customers', label: 'Delete Customers' },
      { key: 'export_customers', label: 'Export Customers' },
    ],
  },
  {
    category: 'Customer Onboarding',
    icon: <MdPersonAdd size={20} />,
    color: '#13c2c2',
    permissions: [
      { key: 'view_onboarding', label: 'View Onboarding' },
      { key: 'create_onboarding', label: 'Create Onboarding' },
      { key: 'update_user_onboarding', label: 'Update User Onboarding' },
    ],
  },
  {
    category: 'Credit Control',
    icon: <MdAttachMoney size={20} />,
    color: '#eb2f96',
    permissions: [
      { key: 'view_credit_control', label: 'View Credit Control' },
      { key: 'manage_credit_limits', label: 'Manage Credit Limits' },
      { key: 'approve_credit', label: 'Approve Credit' },
      { key: 'view_credit_reports', label: 'View Credit Reports' },
    ],
  },
  {
    category: 'Product Configurator',
    icon: <MdBuild size={20} />,
    color: '#faad14',
    permissions: [
      { key: 'view_product_configurator', label: 'View Product Configurator' },
      { key: 'manage_products', label: 'Manage Products' },
      { key: 'manage_pricing', label: 'Manage Pricing' },
      { key: 'manage_options', label: 'Manage Options' },
      { key: 'manage_product_validation', label: 'Manage Product Validation' },
      { key: 'manage_chatbot_flow', label: 'Manage Chatbot Flow' },
    ],
  },
  {
    category: 'Analytics & Reports',
    icon: <MdBarChart size={20} />,
    color: '#2f54eb',
    permissions: [
      { key: 'view_analytics', label: 'View Analytics' },
    ],
  },
  {
    category: 'User Management',
    icon: <MdPeople size={20} />,
    color: '#f5222d',
    permissions: [
      { key: 'view_users', label: 'View Users' },
      { key: 'create_users', label: 'Create Users' },
      { key: 'edit_users', label: 'Edit Users' },
      { key: 'deactivate_users', label: 'Deactivate Users' },
      { key: 'manage_roles', label: 'Manage Roles' },
    ],
  },
  {
    category: 'Settings & Configuration',
    icon: <MdSettings size={20} />,
    color: '#595959',
    permissions: [
      { key: 'view_settings', label: 'View Settings' },
      { key: 'view_permissions', label: 'View Permissions' },
      { key: 'manage_permissions', label: 'Manage Permissions' },
    ],
  },
  {
    category: 'Permission',
    icon: <MdShield size={20} />,
    color: '#8b1538',
    permissions: [
      { key: 'add_roles', label: 'Add Roles' },
      { key: 'update_roles', label: 'Update Roles' },
      { key: 'assign_permission_to_role', label: 'Assign Permission to Role' },
    ],
  },
  {
    category: 'Depot Management',
    icon: <MdWarehouse size={20} />,
    color: '#096dd9',
    permissions: [
      { key: 'view_depots', label: 'View Depots' },
      { key: 'manage_depots', label: 'Manage Depots' },
      { key: 'assign_depots', label: 'Assign Depots' },
    ],
  },
  {
    category: 'Currency Exchange',
    icon: <MdCurrencyExchange size={20} />,
    color: '#389e0d',
    permissions: [
      { key: 'view_currency_exchange', label: 'View Currency Exchange' },
      { key: 'manage_currency_exchange', label: 'Manage Currency Exchange' },
    ],
  },
];

export const PermissionSummaryCard: React.FC = () => {
  const { data: currentUser, isLoading } = useGetCurrentUser();

  const permissionStats = useMemo(() => {
    if (!currentUser?.permission) return { total: 0, granted: 0, denied: 0 };

    const permissions = currentUser.permission;
    const allPermissions = PERMISSION_GROUPS.flatMap(g => g.permissions.map(p => p.key));

    const granted = allPermissions.filter(key => permissions[key] === true).length;
    const total = allPermissions.length;
    const denied = total - granted;

    return { total, granted, denied };
  }, [currentUser]);

  if (isLoading) {
    return (
      <Card>
        <div className="flex justify-center items-center py-8">
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!currentUser?.permission) {
    return (
      <Card>
        <Empty description="No permissions data available" />
      </Card>
    );
  }

  const permissions = currentUser.permission;

  const renderPermissionItem = (permKey: string, label: string) => {
    const hasPermission = permissions[permKey] === true;
    return (
      <div
        key={permKey}
        className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
          hasPermission
            ? 'bg-green-50 border-green-200 hover:bg-green-100'
            : 'bg-white border-gray-200 hover:bg-gray-50'
        }`}
      >
        <div className="flex items-center gap-2">
          {hasPermission ? (
            <MdCheck className="text-green-600" size={18} />
          ) : (
            <MdClose className="text-gray-400" size={18} />
          )}
          <Text className={`text-sm ${hasPermission ? 'font-medium' : ''}`}>{label}</Text>
        </div>
        <div className="flex items-center gap-2">
          <Text
            className={`text-xs font-semibold ${
              hasPermission ? 'text-green-600' : 'text-gray-500'
            }`}
          >
            {hasPermission ? 'Granted' : 'Denied'}
          </Text>
        </div>
      </div>
    );
  };

  return (
    <Card className="shadow-sm">
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
        <Title level={4} className="mb-0">Permission Details</Title>
        <div className="flex gap-2">
          <div className="px-3 py-1 bg-green-50 border border-green-200 rounded-lg">
            <Text className="text-green-700 font-semibold text-sm">
              {permissionStats.granted} Granted
            </Text>
          </div>
          <div className="px-3 py-1 bg-gray-50 border border-gray-200 rounded-lg">
            <Text className="text-gray-700 font-semibold text-sm">
              {permissionStats.denied} Denied
            </Text>
          </div>
          <div className="px-3 py-1 bg-blue-50 border border-blue-200 rounded-lg">
            <Text className="text-blue-700 font-semibold text-sm">
              {permissionStats.total} Total
            </Text>
          </div>
        </div>
      </div>

      <Collapse
        defaultActiveKey={['0']}
        expandIconPosition="end"
        className="bg-transparent"
      >
        {PERMISSION_GROUPS.map((group, index) => {
          const groupPermissions = group.permissions.map(p => permissions[p.key] === true);
          const grantedCount = groupPermissions.filter(Boolean).length;
          const totalCount = group.permissions.length;
          const percentage = Math.round((grantedCount / totalCount) * 100);

          return (
            <Panel
              key={index}
              header={
                <div className="flex items-center gap-3 w-full">
                  <div
                    className="flex items-center justify-center w-8 h-8 rounded-lg"
                    style={{ backgroundColor: `${group.color}15`, color: group.color }}
                  >
                    {group.icon}
                  </div>
                  <Text strong className="text-base">{group.category}</Text>
                  <div className="ml-auto flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Tag
                        color={percentage === 100 ? 'success' : percentage > 0 ? 'warning' : 'default'}
                        className="m-0"
                      >
                        {grantedCount}/{totalCount}
                      </Tag>
                      <div
                        className="text-xs font-semibold px-2 py-1 rounded"
                        style={{
                          backgroundColor: `${group.color}15`,
                          color: group.color,
                        }}
                      >
                        {percentage}%
                      </div>
                    </div>
                  </div>
                </div>
              }
              className="mb-2"
            >
              <div className="space-y-2 pt-2">
                {group.permissions.map((perm) =>
                  renderPermissionItem(perm.key, perm.label)
                )}
              </div>
            </Panel>
          );
        })}
      </Collapse>
    </Card>
  );
};
